import { traceImportAwait } from '@affine/nbstore';
import {
  type DocFrontendState,
  isDocSyncUnavailable,
} from '@affine/nbstore/frontend';
import {
  DatabaseBlockDataSource,
  DatabasePublicationBaseUnavailable,
  type DatabaseRelationInverseEdit,
  type DatabaseRelationInversePlanner,
} from '@blocksuite/affine/blocks/database';
import {
  registerSystemDocBarrier,
  registerSystemDocLoader,
  runSystemDocBarrier,
  type SystemDocBarrier,
  type SystemDocLoadContext,
} from '@blocksuite/store';
import { PublicationError } from '@streamlain/doc-publication-contract';
import type { Observable } from 'rxjs';

import { findDatabaseByBlockId } from '../../../blocksuite/database-block/properties/relation/database-discovery-bridge';
import {
  collectDatabaseRootSubdocIdsFromDatabaseBlocks,
  collectDatabaseSubdocIdsFromDatabaseBlocks,
  collectDatabaseSubdocIdsFromDataSourceRefs,
  collectInitialDatabaseSubdocIdsFromDatabaseBlocks,
  collectLocalFirstDatabaseSubdocIdsFromDatabaseBlocks,
  collectLocalFirstPagePartDocIds,
  DATABASE_BLOCK_FLAVOUR,
  type DatabaseSubdocBlockIdentity,
  type DatabaseSubdocDataSourceRef,
  dataSourceRefToDatabaseBlockIdentity,
  isDatabaseRootDocId,
  isDiscoverableDatabaseSubdocIdentity,
  readDatabaseSubdocDirectory,
  toDatabaseDocId,
} from './database-subdoc-identities';
export {
  collectDatabaseRootSubdocIdsFromDatabaseBlocks,
  collectDatabaseSubdocIdsFromDatabaseBlocks,
  collectDatabaseSubdocIdsFromDataSourceRefs,
  collectInitialDatabaseSubdocIdsFromDatabaseBlocks,
  collectLocalFirstDatabaseSubdocIdsFromDatabaseBlocks,
  collectLocalFirstPagePartDocIds,
  type DatabaseSubdocBlockIdentity,
  type DatabaseSubdocDataSourceRef,
} from './database-subdoc-identities';
import type { Store } from '@blocksuite/affine/store';
import {
  classifyPageSubdocId,
  createDatabaseShardManifest,
  createServerPagePartDocId,
  databaseShardDirectoryMap,
  type DatabaseShardDirectoryPartDocs,
  databaseShardDirectoryPartMap,
  normalizeDatabaseBlockId,
  normalizeDatabaseShardManifest,
  PAGE_DOCUMENT_PART_ID,
  pageBlockIdsOfKind,
  pagePlacedBlockIds,
  PageReadPartMissingError,
  pageReadSource,
  readDatabaseShardDirectoryBlock,
  readDatabaseShardDirectoryBlockPartDocIds,
  readDatabaseShardDirectoryProps,
  readPage,
  selectDatabaseShardsMayContainRow,
  storedDatabaseShardDirectory,
} from '@streamlain/database-identity';
import { applyUpdate, Array as YArray, Doc as YDoc, Map as YMap } from 'yjs';

import { createDatabasePublicationViewObserver } from '../../../blocksuite/block-suite-editor/database-publication-view';
import { isDatabaseRootMissing } from '../../../blocksuite/block-suite-editor/database-root-readiness';
import type { WorkspaceService } from '../../workspace';
import { collectSnapshotExportBlobIds } from './database-subdoc-export-blobs';
import {
  acknowledgeDatabaseSubdoc,
  releaseAcknowledgedSystemDocCache,
  retainDatabaseSubdoc,
} from './database-subdoc-resources';

export type DatabaseSystemDocAccessBinding = {
  databaseId: string;
  rootDocId: string;
  accessDocId: string;
  blockId: string;
  manifest: NonNullable<ReturnType<typeof normalizeDatabaseShardManifest>>;
};

type DatabaseSubdocPageDiscoverySnapshot = {
  blockCount: number;
  databaseBlockCount: number;
  databaseBlocksWithId: number;
  databaseBlocksWithManifest: number;
  databaseBlocksWithRootDocId: number;
  malformedBlockCount: number;
  sampleDatabaseBlocks: Array<{
    hasDatabaseId: boolean;
    hasManifest: boolean;
    hasRootDocId: boolean;
  }>;
  sampleFlavours: string[];
};

const DATABASE_LOAD_MARK_PREFIX = 'sl-database-load';
const DATABASE_DISCOVERY_SAMPLE_LIMIT = 5;
const SNAPSHOT_EXPORT_DATABASE_SUBDOC_LOAD_TIMEOUT_MS = 15_000;
const ACTIVE_DATABASE_SUBDOC_PRIORITY = 110;
const VISIBLE_PAGE_PART_PRIORITY = 95;

/** Page parts in sight load before the other system documents. */
function systemDocLoadPriority(context: SystemDocLoadContext): number {
  return context.kind === 'page-part' && context.reason === 'viewport'
    ? VISIBLE_PAGE_PART_PRIORITY
    : 90;
}

export type SnapshotExportMode =
  | 'single-doc'
  | 'multi-doc'
  | 'workspace'
  | 'recovery-current-state';

type DatabaseSubdocDocEngine = {
  addDoc(
    docId: string,
    source: 'database-subdoc' | 'workspace-snapshot-export'
  ): void;
  addPriority(docId: string, priority: number): () => void;
  debugDocState(docId: string): Promise<Array<{ reasons: string[] }>>;
  flushAndWaitForSynced?(docId: string, abort?: AbortSignal): Promise<void>;
  pullRemote?(docId: string, signal?: AbortSignal): Promise<void>;
  waitForDocLoaded(docId: string, abort?: AbortSignal): Promise<void>;
  waitForDocReady?(docId: string, abort?: AbortSignal): Promise<void>;
  waitForSynced?(docId?: string, abort?: AbortSignal): Promise<void>;
  waitForUpdated?(docId?: string, abort?: AbortSignal): Promise<void>;
  releaseDoc(
    docId: string,
    source:
      | 'workspace-doc-load'
      | 'workspace-snapshot-export'
      | 'database-subdoc'
  ): Promise<void>;
};

type DatabaseSubdocExportLoadPlan = {
  eagerDocIds: Set<string>;
  emptyRowShardDocIds: Set<string>;
  rootDocIds: Set<string>;
  rowShardCandidateGroups: string[][];
  /** Directory part documents; the shard lists are read from them. */
  directoryPartDocIds: Set<string>;
  /**
   * Directory part documents a shard list still waits for, as of the stores
   * the plan was read from.
   */
  missingDirectoryPartDocIds: Set<string>;
};

export type ExportScopePlan = {
  mode: SnapshotExportMode;
  sourceDocIds: string[];
  eagerDocIds: string[];
  rootDocIds: string[];
  rowShardCandidateGroups: string[][];
  requiredDocIds: string[];
  blobIds: string[];
  warnings: string[];
  timeoutMs: number;
};

function markDatabaseLoad(event: string, data: Record<string, unknown>): void {
  if (
    typeof performance === 'undefined' ||
    typeof performance.mark !== 'function'
  ) {
    return;
  }

  try {
    performance.mark(
      `${DATABASE_LOAD_MARK_PREFIX}:${event}:${JSON.stringify(data)}`
    );
  } catch {
    // Performance marks are diagnostics only.
  }
}

function loadDatabaseWorkspaceDoc(
  page: Store,
  docId: string,
  reason: string
): void {
  loadDatabaseWorkspaceCollectionDoc(page.workspace, docId, reason, page.id);
}

/** Registers `docId` in the workspace collection if needed and loads it. */
export function loadDatabaseWorkspaceCollectionDoc(
  collection: Store['workspace'],
  docId: string,
  reason: string,
  pageId?: string
): void {
  const workspace = collection as {
    createDoc?: (id: string) => { load?: () => unknown; ready?: boolean };
    getDoc?: (
      id: string
    ) => { load?: () => unknown; ready?: boolean } | null | undefined;
    registerDoc?: (id: string) => unknown;
  };
  let doc = workspace.getDoc?.(docId);
  if (!doc) {
    workspace.registerDoc?.(docId);
    doc = workspace.getDoc?.(docId);
  }
  if (!doc) {
    doc = workspace.createDoc?.(docId);
  }

  if (doc && !doc.ready) {
    doc.load?.();
  }

  markDatabaseLoad('preload-workspace-doc-load', {
    docId,
    pageId,
    reason,
    workspaceId: collection.id,
  });
}

/**
 * Selects the bounded database working set that follows an opened PageDoc into
 * the existing local DocStorage. DocStorage persists every connected subdoc,
 * so reopening a cached PageDoc can hydrate these rows locally before remote
 * reconciliation. The global visible-row limit prevents a large database from
 * being materialized wholesale merely because its host document was opened.
 */

function createDatabaseSubdocExportLoadPlan(): DatabaseSubdocExportLoadPlan {
  return {
    eagerDocIds: new Set(),
    emptyRowShardDocIds: new Set(),
    rootDocIds: new Set(),
    rowShardCandidateGroups: [],
    directoryPartDocIds: new Set(),
    missingDirectoryPartDocIds: new Set(),
  };
}

function addCandidateGroup(
  groups: string[][],
  candidates: Iterable<unknown>
): void {
  const unique = new Set<string>();
  for (const candidate of candidates) {
    const docId = toDatabaseDocId(candidate);
    if (docId) {
      unique.add(docId);
    }
  }
  if (unique.size > 0) {
    groups.push(Array.from(unique));
  }
}

function addDatabaseIdentitiesToExportLoadPlan(
  plan: DatabaseSubdocExportLoadPlan,
  identities: Iterable<DatabaseSubdocBlockIdentity>,
  docs: DatabaseShardDirectoryPartDocs
): void {
  for (const block of identities) {
    if (!isDiscoverableDatabaseSubdocIdentity(block)) {
      continue;
    }

    const rootDocId = toDatabaseDocId(block.databaseRootDocId);
    if (!rootDocId) {
      throw new Error(`Database block "${block.id}" names no root document.`);
    }
    plan.eagerDocIds.add(rootDocId);
    plan.rootDocIds.add(rootDocId);

    const directory = readDatabaseSubdocDirectory(block, docs);
    const manifestRootDocId = toDatabaseDocId(
      directory?.loaded.databaseRootDocId
    );
    if (manifestRootDocId) {
      plan.eagerDocIds.add(manifestRootDocId);
      plan.rootDocIds.add(manifestRootDocId);
    }
    for (const partDocId of directory?.partDocIds ?? []) {
      plan.eagerDocIds.add(partDocId);
      plan.directoryPartDocIds.add(partDocId);
    }
    // A shard list is complete only with every directory part: until then
    // the export loads the parts and plans the shards again.
    for (const partDocId of directory?.missingPartDocIds ?? []) {
      plan.missingDirectoryPartDocIds.add(partDocId);
    }
    if (directory && directory.missingPartDocIds.length === 0) {
      for (const shard of directory.loaded.shards) {
        if (shard.rowCount <= 0) {
          // Empty shards carry no row payload, so they cannot satisfy the
          // content-readiness wait. They still need a registered Store so
          // the archive remains manifest-complete for snapshot import.
          plan.eagerDocIds.add(shard.serverDocId);
          plan.emptyRowShardDocIds.add(shard.serverDocId);
          continue;
        }
        addCandidateGroup(plan.rowShardCandidateGroups, [shard.serverDocId]);
      }
    }
  }
}

function collectDatabaseSubdocExportLoadPlanFromPage(
  page: Store
): DatabaseSubdocExportLoadPlan {
  const { identities } = readDatabaseSubdocPageDiscovery(page);
  const plan = createDatabaseSubdocExportLoadPlan();
  addDatabaseIdentitiesToExportLoadPlan(plan, identities, page.workspace);
  return plan;
}

/** Export load plan of the database blocks of `stores`, read afresh. */
function collectDatabaseSubdocExportLoadPlanFromStores(
  stores: Iterable<Store>
): DatabaseSubdocExportLoadPlan {
  const plan = createDatabaseSubdocExportLoadPlan();
  for (const store of stores) {
    mergeDatabaseSubdocExportLoadPlan(
      plan,
      collectDatabaseSubdocExportLoadPlanFromPage(store)
    );
  }
  return plan;
}

/** Fails an export whose shard list would stay partial. */
function assertDatabaseDirectoriesLoaded(
  plan: DatabaseSubdocExportLoadPlan
): void {
  if (plan.missingDirectoryPartDocIds.size === 0) return;
  throw new Error(
    `Cannot export sharded database because these directory part docs did not load: ${Array.from(plan.missingDirectoryPartDocIds).join(', ')}`
  );
}

function mergeDatabaseSubdocExportLoadPlan(
  target: DatabaseSubdocExportLoadPlan,
  source: DatabaseSubdocExportLoadPlan
): void {
  source.eagerDocIds.forEach(docId => target.eagerDocIds.add(docId));
  source.emptyRowShardDocIds.forEach(docId =>
    target.emptyRowShardDocIds.add(docId)
  );
  source.rootDocIds.forEach(docId => target.rootDocIds.add(docId));
  target.rowShardCandidateGroups.push(...source.rowShardCandidateGroups);
  source.directoryPartDocIds.forEach(docId =>
    target.directoryPartDocIds.add(docId)
  );
  source.missingDirectoryPartDocIds.forEach(docId =>
    target.missingDirectoryPartDocIds.add(docId)
  );
}

function dedupeCandidateGroups(groups: string[][]): string[][] {
  const seen = new Set<string>();
  const result: string[][] = [];
  for (const group of groups) {
    const normalized = Array.from(new Set(group)).sort();
    if (normalized.length === 0) {
      continue;
    }
    const key = normalized.join('\0');
    if (seen.has(key)) {
      continue;
    }
    seen.add(key);
    result.push(normalized);
  }
  return result;
}

function waitForDatabaseDocReady(
  engineDoc: DatabaseSubdocDocEngine,
  docId: string,
  abort?: AbortSignal
): Promise<void> {
  return engineDoc.waitForDocReady
    ? engineDoc.waitForDocReady(docId, abort)
    : engineDoc.waitForDocLoaded(docId, abort);
}

function waitForAnyDocReady(
  engineDoc: DatabaseSubdocDocEngine,
  candidateDocIds: readonly string[],
  timeoutMs: number
): Promise<string> {
  if (candidateDocIds.length === 0) {
    return Promise.reject(new Error('No database subdoc candidates to load.'));
  }

  const abort = new AbortController();
  let settled = false;
  let remaining = candidateDocIds.length;
  let timeoutId: ReturnType<typeof setTimeout> | null = null;

  return new Promise((resolve, reject) => {
    const finish = (callback: () => void) => {
      if (settled) {
        return;
      }
      settled = true;
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
      abort.abort();
      callback();
    };

    timeoutId = setTimeout(() => {
      finish(() => {
        reject(
          new Error(
            `Cannot export sharded database because none of these database docs loaded within ${timeoutMs}ms: ${candidateDocIds.join(', ')}`
          )
        );
      });
    }, timeoutMs);

    for (const docId of candidateDocIds) {
      waitForDatabaseDocReady(engineDoc, docId, abort.signal)
        .then(() => {
          finish(() => resolve(docId));
        })
        .catch(() => {
          if (settled) {
            return;
          }
          remaining -= 1;
          if (remaining === 0) {
            finish(() => {
              reject(
                new Error(
                  `Cannot export sharded database because none of these database docs loaded: ${candidateDocIds.join(', ')}`
                )
              );
            });
          }
        });
    }
  });
}

async function loadDatabaseSubdocGroupsForSnapshotExport(
  engineDoc: DatabaseSubdocDocEngine,
  groups: readonly string[][],
  timeoutMs: number
): Promise<void> {
  for (const group of groups) {
    await waitForAnyDocReady(engineDoc, group, timeoutMs);
  }
}

export async function prepareDatabaseSubdocsForSnapshotExport(
  pages: readonly Store[],
  workspaceService: WorkspaceService,
  options: {
    timeoutMs?: number;
    mode?: SnapshotExportMode;
    workspaceStream?: {
      claimDocId(docId: string): Promise<boolean>;
      onStoreReady(store: Store): Promise<void>;
    };
  } = {}
): Promise<ExportScopePlan> {
  const timeoutMs =
    options.timeoutMs ?? SNAPSHOT_EXPORT_DATABASE_SUBDOC_LOAD_TIMEOUT_MS;
  const mode = options.mode ?? 'single-doc';
  const engineDoc = workspaceService.workspace.engine.doc;
  const cleanups: Array<() => void> = [];
  const enqueued = new Set<string>();
  const initiallyWorkspaceLoadRequested = new Set<string>();
  const plan = createDatabaseSubdocExportLoadPlan();
  const requestSource =
    mode === 'workspace' ? 'workspace-snapshot-export' : 'database-subdoc';

  // An export names every database of its pages, also those in parts outside
  // the view: load the parts that hold their blocks first.
  await Promise.all(
    pages.map(page =>
      page.pageStructure.loadBlockIdsOfKind(DATABASE_BLOCK_FLAVOUR)
    )
  );
  for (const page of pages) {
    mergeDatabaseSubdocExportLoadPlan(
      plan,
      collectDatabaseSubdocExportLoadPlanFromPage(page)
    );
  }

  if (mode === 'workspace' && options.workspaceStream) {
    const sourcePage = pages[0];
    const consume = async (
      docId: string,
      waitForReady = true
    ): Promise<Store | null> => {
      if (!(await options.workspaceStream!.claimDocId(docId))) return null;
      const states = await engineDoc.debugDocState(docId);
      const initiallyHadWorkspaceLoadRequest = states.some(state =>
        state.reasons.includes('request:workspace-doc-load')
      );
      engineDoc.addDoc(docId, 'workspace-snapshot-export');
      const releasePriority = engineDoc.addPriority(docId, 95);
      try {
        if (sourcePage) {
          loadDatabaseWorkspaceDoc(sourcePage, docId, 'snapshot-export');
        }
        if (waitForReady) {
          await waitForAnyDocReady(engineDoc, [docId], timeoutMs);
        }
        const store = sourcePage?.workspace
          .getDoc(docId)
          ?.getStore({ id: docId });
        if (!store) {
          throw new Error(
            `Cannot export database doc because it has no loaded store: ${docId}`
          );
        }
        await options.workspaceStream!.onStoreReady(store);
        return store;
      } finally {
        releasePriority();
        await engineDoc.releaseDoc(docId, 'workspace-snapshot-export');
        if (!initiallyHadWorkspaceLoadRequest) {
          await engineDoc.releaseDoc(docId, 'workspace-doc-load');
        }
      }
    };

    const rootStores: Store[] = [];
    for (const rootDocId of plan.rootDocIds) {
      const rootStore = await consume(rootDocId);
      if (rootStore) {
        rootStores.push(rootStore);
        mergeDatabaseSubdocExportLoadPlan(
          plan,
          collectDatabaseSubdocExportLoadPlanFromPage(rootStore)
        );
      }
    }
    // The shard lists are read from the directory parts: export them, then
    // plan the shards of the complete lists.
    for (const docId of plan.directoryPartDocIds) {
      await consume(docId);
    }
    const directories = collectDatabaseSubdocExportLoadPlanFromStores([
      ...pages,
      ...rootStores,
    ]);
    assertDatabaseDirectoriesLoaded(directories);
    mergeDatabaseSubdocExportLoadPlan(plan, directories);
    for (const docId of plan.emptyRowShardDocIds) {
      await consume(docId, false);
    }
    for (const group of dedupeCandidateGroups(plan.rowShardCandidateGroups)) {
      let loaded = false;
      let lastError: unknown;
      for (const docId of group) {
        try {
          loaded = (await consume(docId)) !== null || loaded;
          if (loaded) break;
        } catch (error) {
          lastError = error;
        }
      }
      if (!loaded && lastError) throw lastError;
    }
    return toExportScopePlan(mode, pages, plan, timeoutMs);
  }

  const enqueue = async (docId: string) => {
    if (enqueued.has(docId)) {
      return;
    }
    enqueued.add(docId);
    if (mode === 'workspace') {
      const states = await engineDoc.debugDocState(docId);
      if (
        states.some(state =>
          state.reasons.includes('request:workspace-doc-load')
        )
      ) {
        initiallyWorkspaceLoadRequested.add(docId);
      }
    }
    engineDoc.addDoc(docId, requestSource);
    cleanups.push(engineDoc.addPriority(docId, 95));
    const sourcePage = pages[0];
    if (sourcePage) {
      loadDatabaseWorkspaceDoc(sourcePage, docId, 'snapshot-export');
    }
  };

  try {
    // Source stores are already loaded: callers obtained them from the active
    // workspace before entering this preflight. Waiting for their network
    // engine state makes an otherwise local snapshot export depend on backend
    // availability. Only discovered database subdocs need hydration here.
    for (const docId of plan.eagerDocIds) await enqueue(docId);
    await loadDatabaseSubdocGroupsForSnapshotExport(
      engineDoc,
      Array.from(plan.rootDocIds, docId => [docId]),
      timeoutMs
    );

    const readRootStores = () => {
      const rootStores: Store[] = [];
      for (const rootDocId of plan.rootDocIds) {
        const rootStore = pages[0]?.workspace.getDoc(rootDocId)?.getStore({
          id: rootDocId,
        });
        if (!rootStore) {
          continue;
        }
        rootStores.push(rootStore);
        mergeDatabaseSubdocExportLoadPlan(
          plan,
          collectDatabaseSubdocExportLoadPlanFromPage(rootStore)
        );
      }
      return rootStores;
    };
    readRootStores();

    // The shard lists are read from the directory parts: load them, then
    // plan the shards of the complete lists.
    if (plan.directoryPartDocIds.size > 0) {
      for (const docId of plan.directoryPartDocIds) await enqueue(docId);
      await loadDatabaseSubdocGroupsForSnapshotExport(
        engineDoc,
        Array.from(plan.directoryPartDocIds, docId => [docId]),
        timeoutMs
      );
      const directories = collectDatabaseSubdocExportLoadPlanFromStores([
        ...pages,
        ...readRootStores(),
      ]);
      assertDatabaseDirectoriesLoaded(directories);
      mergeDatabaseSubdocExportLoadPlan(plan, directories);
    }

    for (const docId of plan.eagerDocIds) await enqueue(docId);
    for (const group of plan.rowShardCandidateGroups) {
      for (const docId of group) await enqueue(docId);
    }
    await loadDatabaseSubdocGroupsForSnapshotExport(
      engineDoc,
      dedupeCandidateGroups(plan.rowShardCandidateGroups),
      timeoutMs
    );
    return toExportScopePlan(mode, pages, plan, timeoutMs);
  } finally {
    cleanups.splice(0).forEach(cleanup => cleanup());
    if (mode === 'workspace') {
      for (const docId of enqueued) {
        await engineDoc.releaseDoc(docId, 'workspace-snapshot-export');
        if (!initiallyWorkspaceLoadRequested.has(docId)) {
          await engineDoc.releaseDoc(docId, 'workspace-doc-load');
        }
      }
    }
  }
}

function toExportScopePlan(
  mode: SnapshotExportMode,
  pages: readonly Store[],
  plan: DatabaseSubdocExportLoadPlan,
  timeoutMs: number
): ExportScopePlan {
  const sourceDocIds = Array.from(new Set(pages.map(page => page.id))).sort();
  const eagerDocIds = Array.from(plan.eagerDocIds).sort();
  const rootDocIds = Array.from(plan.rootDocIds).sort();
  const rowShardCandidateGroups = dedupeCandidateGroups(
    plan.rowShardCandidateGroups
  );
  const requiredDocIds = Array.from(
    new Set([
      ...sourceDocIds,
      ...eagerDocIds,
      ...rowShardCandidateGroups.flat(),
    ])
  ).sort();
  const blobIds = collectSnapshotExportBlobIds(
    pages,
    rootDocIds,
    rowShardCandidateGroups.flat()
  );

  return {
    mode,
    sourceDocIds,
    eagerDocIds,
    rootDocIds,
    rowShardCandidateGroups,
    requiredDocIds,
    blobIds,
    warnings: [],
    timeoutMs,
  };
}

function addSampleFlavour(sampleFlavours: string[], flavour: unknown): void {
  if (
    typeof flavour !== 'string' ||
    sampleFlavours.includes(flavour) ||
    sampleFlavours.length >= DATABASE_DISCOVERY_SAMPLE_LIMIT
  ) {
    return;
  }
  sampleFlavours.push(flavour);
}

function readDatabaseSubdocPageDiscovery(page: Store): {
  identities: DatabaseSubdocBlockIdentity[];
  snapshot: DatabaseSubdocPageDiscoverySnapshot;
} {
  const structure = page.pageStructure;
  const identities: DatabaseSubdocBlockIdentity[] = [];
  const snapshot: DatabaseSubdocPageDiscoverySnapshot = {
    blockCount: pagePlacedBlockIds(structure.tree()).length,
    databaseBlockCount: 0,
    databaseBlocksWithId: 0,
    databaseBlocksWithManifest: 0,
    databaseBlocksWithRootDocId: 0,
    malformedBlockCount: 0,
    sampleDatabaseBlocks: [],
    sampleFlavours: [],
  };

  for (const blockId of pagePlacedBlockIds(structure.tree())) {
    const block = structure.blockFields(blockId);
    // A placed block without fields lies in a part not loaded yet.
    if (block === undefined) continue;
    if (!(block instanceof YMap)) {
      snapshot.malformedBlockCount += 1;
      continue;
    }
    const identity: DatabaseSubdocBlockIdentity = {
      id: block.get('sys:id') ?? blockId,
      databaseId: block.get('prop:databaseId'),
      databaseRootDocId: block.get('prop:databaseRootDocId'),
      databaseShardManifest: databaseShardDirectoryMap(block),
      flavour: block.get('sys:flavour'),
      managedBy: block.get('prop:managedBy'),
      visible: block.get('prop:visible'),
    };
    addSampleFlavour(snapshot.sampleFlavours, identity.flavour);
    identities.push(identity);

    if (!isDiscoverableDatabaseSubdocIdentity(identity)) {
      continue;
    }

    snapshot.databaseBlockCount += 1;
    const hasDatabaseId = typeof identity.databaseId === 'string';
    const hasRootDocId = typeof identity.databaseRootDocId === 'string';
    const hasManifest = storedDatabaseShardDirectory(identity) != null;
    if (hasDatabaseId) {
      snapshot.databaseBlocksWithId += 1;
    }
    if (hasRootDocId) {
      snapshot.databaseBlocksWithRootDocId += 1;
    }
    if (hasManifest) {
      snapshot.databaseBlocksWithManifest += 1;
    }
    if (
      snapshot.sampleDatabaseBlocks.length < DATABASE_DISCOVERY_SAMPLE_LIMIT
    ) {
      snapshot.sampleDatabaseBlocks.push({
        hasDatabaseId,
        hasManifest,
        hasRootDocId,
      });
    }
  }

  return {
    identities,
    snapshot,
  };
}

export function inspectDatabaseSubdocDiscoveryFromPage(
  page: Store
): DatabaseSubdocPageDiscoverySnapshot {
  return readDatabaseSubdocPageDiscovery(page).snapshot;
}

export function collectDatabaseSubdocIdsFromPage(page: Store): string[] {
  const { identities } = readDatabaseSubdocPageDiscovery(page);

  return collectDatabaseSubdocIdsFromDatabaseBlocks(
    identities,
    page.workspace.id,
    page.workspace
  );
}

/** Database id a database block names; every database block names one. */
function requireBlockDatabaseId(block: {
  model: { id: string; props: unknown };
}) {
  const props = block.model.props as Record<string, unknown>;
  const databaseId =
    typeof props.databaseId === 'string'
      ? normalizeDatabaseBlockId(props.databaseId)
      : null;
  if (!databaseId) {
    throw new Error(`Database block "${block.model.id}" names no database id.`);
  }
  return databaseId;
}

/**
 * Access bindings of the databases of `page`, each with the complete directory
 * of its local root. Without that root or directory no binding is guessed.
 */
export function collectDatabaseSystemDocAccessBindingsFromPage(
  page: Store,
  resolveStore: (docId: string) => Store | null,
  requestedDatabaseIds?: ReadonlySet<string>
): DatabaseSystemDocAccessBinding[] {
  const bindings: DatabaseSystemDocAccessBinding[] = [];

  for (const block of page.getBlocksByFlavour(DATABASE_BLOCK_FLAVOUR)) {
    const props = block.model.props as Record<string, unknown>;
    const databaseId = requireBlockDatabaseId(block);
    if (requestedDatabaseIds && !requestedDatabaseIds.has(databaseId)) {
      continue;
    }

    const rootDocId = props.databaseRootDocId;
    if (typeof rootDocId !== 'string' || !rootDocId) {
      throw new Error(`Database "${databaseId}" names no root document.`);
    }
    const rootBlock = resolveStore(rootDocId)
      ?.getBlocksByFlavour(DATABASE_BLOCK_FLAVOUR)
      // The root block of a database carries the database id as its block id.
      .find(candidate => candidate.model.id === databaseId);
    const currentManifest = rootBlock
      ? readDatabaseShardDirectoryProps(
          rootBlock.model.props as Record<string, unknown>,
          page.workspace
        )
      : undefined;
    // Without its local root and complete directory the shards are unknown.
    if (!currentManifest) throw new DatabasePublicationBaseUnavailable();
    const manifest = createDatabaseShardManifest({
      workspaceId: page.workspace.id,
      databaseId,
      databaseRootDocId: rootDocId,
      targetBytes: currentManifest.targetBytes,
      hardLimitBytes: currentManifest.hardLimitBytes,
      shards: currentManifest.shards,
    });

    bindings.push({
      databaseId,
      rootDocId,
      accessDocId: page.id,
      blockId: block.model.id,
      manifest,
    });
  }

  return bindings;
}

export function collectDatabaseRootSubdocIdsFromPage(page: Store): string[] {
  const { identities } = readDatabaseSubdocPageDiscovery(page);

  return collectDatabaseRootSubdocIdsFromDatabaseBlocks(
    identities,
    page.workspace.id
  );
}

export function prioritizeDatabaseSubdocsForBlock(
  page: Store,
  blockId: string,
  workspaceService: WorkspaceService
): () => void {
  const block = page.getBlock(blockId);
  if (!block || block.model.flavour !== DATABASE_BLOCK_FLAVOUR) {
    return () => {};
  }

  const props = block.model.props as Record<string, unknown>;
  const docIds = collectInitialDatabaseSubdocIdsFromDatabaseBlocks(
    [
      {
        id: block.model.id,
        databaseId: requireBlockDatabaseId(block),
        databaseRootDocId: props.databaseRootDocId,
        databaseShardManifest: storedDatabaseShardDirectory(props),
        flavour: block.model.flavour,
        managedBy: props.managedBy,
        visible: props.visible,
      },
    ],
    page.workspace.id,
    page.workspace
  );
  const engineDoc = workspaceService.workspace.engine.doc;
  const cleanups: Array<() => void> = [];

  for (const docId of docIds) {
    cleanups.push(
      engineDoc.addPriority(docId, ACTIVE_DATABASE_SUBDOC_PRIORITY)
    );
    cleanups.push(retainDatabaseSubdoc(engineDoc, docId));
    loadDatabaseWorkspaceDoc(page, docId, 'active-database');
  }

  markDatabaseLoad('preload-active-database', {
    blockId,
    count: docIds.length,
    pageId: page.id,
    workspaceId: page.workspace.id,
  });

  return () => cleanups.splice(0).forEach(cleanup => cleanup());
}

export function prioritizeDatabaseRowShardForFocus(
  page: Store,
  blockId: string,
  rowShardDocId: string,
  workspaceService: WorkspaceService,
  onReady: () => void
): () => void {
  const block = page.getBlock(blockId);
  if (!block || block.model.flavour !== DATABASE_BLOCK_FLAVOUR) {
    throw new Error(
      `Cannot focus database row shard without its database block: block=${blockId} shard=${rowShardDocId}`
    );
  }

  const workspaceId = page.workspace.id;
  if (workspaceService.workspace.id !== workspaceId) {
    throw new Error(
      `Database focus workspace mismatch: page=${workspaceId} service=${workspaceService.workspace.id}`
    );
  }
  const databaseId = requireBlockDatabaseId(block);
  const parsedRowShard = classifyPageSubdocId(rowShardDocId, workspaceId);
  if (
    parsedRowShard?.kind !== 'database-row-shard' ||
    parsedRowShard.local ||
    parsedRowShard.databaseId !== databaseId
  ) {
    throw new Error(
      `Database focus row shard locator is stale: block=${blockId} shard=${rowShardDocId}`
    );
  }

  const engineDoc = workspaceService.workspace.engine.doc;
  const abort = new AbortController();
  const cleanupDoc = retainDatabaseSubdoc(engineDoc, rowShardDocId);
  const cleanupPriority = engineDoc.addPriority(
    rowShardDocId,
    ACTIVE_DATABASE_SUBDOC_PRIORITY
  );
  loadDatabaseWorkspaceDoc(page, rowShardDocId, 'database-focus');

  void waitForDatabaseDocReady(engineDoc, rowShardDocId, abort.signal)
    .then(() => {
      if (!abort.signal.aborted) {
        onReady();
      }
    })
    .catch(error => {
      if (abort.signal.aborted) {
        return;
      }
      console.error(
        `Failed to hydrate database focus shard "${rowShardDocId}"`,
        error
      );
    });

  return () => {
    abort.abort();
    cleanupPriority();
    cleanupDoc();
  };
}

function collectInitialDatabaseSubdocIdsAndDiscoveryFromPage(page: Store): {
  discovery: DatabaseSubdocPageDiscoverySnapshot;
  localFirstSubdocIds: string[];
  subdocIds: string[];
} {
  const { identities, snapshot } = readDatabaseSubdocPageDiscovery(page);

  return {
    discovery: snapshot,
    localFirstSubdocIds: [
      ...collectLocalFirstDatabaseSubdocIdsFromDatabaseBlocks(
        identities,
        page.workspace.id,
        page.workspace
      ),
      ...collectLocalFirstPagePartDocIds(page.spaceDoc, page.workspace.id),
    ],
    subdocIds: collectInitialDatabaseSubdocIdsFromDatabaseBlocks(
      identities,
      page.workspace.id,
      page.workspace
    ),
  };
}

function markDatabaseSubdocDiscovery(
  page: Store,
  discovery: DatabaseSubdocPageDiscoverySnapshot,
  subdocCount: number
): void {
  markDatabaseLoad('preload-page-discovery', {
    ...discovery,
    pageId: page.id,
    subdocCount,
    workspaceId: page.workspace.id,
  });
}

/**
 * Enqueues the database documents of `page` again whenever one of its blocks
 * or a directory part document among `initialSubdocIds` or the later finds
 * changes: a part that arrives names the row shards of its rows.
 */
function observeDatabaseSubdocDiscovery(
  page: Store,
  enqueue: (docId: string, flushReason?: string, priority?: number) => void,
  getEnqueuedCount: () => number,
  initialSubdocIds: readonly string[]
): () => void {
  const structure = page.pageStructure;

  let marked = false;
  let observing = true;
  const blockObservers = new Map<YMap<unknown>, () => void>();
  const syncBlockObservers = () => {
    const currentBlocks = new Set<YMap<unknown>>();
    for (const id of pagePlacedBlockIds(structure.tree())) {
      const block = structure.blockFields(id);
      if (!(block instanceof YMap)) {
        continue;
      }
      currentBlocks.add(block);
      if (blockObservers.has(block) || typeof block.observe !== 'function') {
        continue;
      }
      const blockObserver = () => observer();
      block.observe(blockObserver);
      blockObservers.set(block, () => block.unobserve(blockObserver));
    }
    for (const [block, dispose] of blockObservers) {
      if (currentBlocks.has(block)) {
        continue;
      }
      dispose();
      blockObservers.delete(block);
    }
  };
  const partObservers = new Map<string, () => void>();
  const syncPartObservers = (subdocIds: readonly string[]) => {
    for (const docId of subdocIds) {
      if (
        partObservers.has(docId) ||
        classifyPageSubdocId(docId, page.workspace.id)?.kind !==
          'database-directory-part'
      )
        continue;
      const doc = page.workspace.getDoc(docId)?.spaceDoc;
      if (!doc) continue;
      const onUpdate = () => observer();
      doc.on('update', onUpdate);
      partObservers.set(docId, () => doc.off('update', onUpdate));
    }
  };
  const observer = () => {
    if (!observing) return;
    syncBlockObservers();
    const { discovery, localFirstSubdocIds, subdocIds } =
      collectInitialDatabaseSubdocIdsAndDiscoveryFromPage(page);
    if (discovery.databaseBlockCount === 0) {
      return;
    }
    const enqueuedBefore = getEnqueuedCount();
    subdocIds.forEach(docId => enqueue(docId, 'root-discovery'));
    localFirstSubdocIds.forEach(docId => enqueue(docId, undefined, 20));
    syncPartObservers(subdocIds);
    const enqueuedAfter = getEnqueuedCount();
    if (!marked) {
      marked = true;
      markDatabaseLoad('preload-page-databases-observed', {
        ...discovery,
        pageId: page.id,
        subdocCount: subdocIds.length,
        workspaceId: page.workspace.id,
      });
    }
    markDatabaseLoad('preload-page-observed-enqueued', {
      count: subdocIds.length,
      enqueuedCount: getEnqueuedCount(),
      newlyEnqueuedCount: enqueuedAfter - enqueuedBefore,
      pageId: page.id,
      workspaceId: page.workspace.id,
    });
  };
  // The fields of a block arrive with its page part: discover again when the
  // blocks of a loaded part change, its first content included.
  const pagePartObservers = new Map<string, () => void>();
  syncBlockObservers();
  const disposeStructure = structure.subscribe(observer);
  const disposePageParts = structure.observeParts((part, loaded, partId) => {
    pagePartObservers.get(partId)?.();
    pagePartObservers.delete(partId);
    if (!loaded) return;
    const onBlocks = () => observer();
    part.blocks.observe(onBlocks);
    pagePartObservers.set(partId, () => part.blocks.unobserve(onBlocks));
  });
  syncPartObservers(initialSubdocIds);

  return () => {
    if (!observing) {
      return;
    }
    observing = false;
    disposeStructure();
    disposePageParts();
    for (const dispose of blockObservers.values()) {
      dispose();
    }
    blockObservers.clear();
    for (const dispose of partObservers.values()) dispose();
    partObservers.clear();
  };
}

export function waitForPagePartSnapshot(
  page: Store,
  signal: AbortSignal
): Promise<void> {
  return runSystemDocBarrier(page, {
    rootDocId: page.id,
    docIds: collectLocalFirstPagePartDocIds(page.spaceDoc, page.workspace.id),
    signal,
  });
}

export function bindPagePartOfflineBarrier(
  page: Store,
  port: {
    state$: Observable<
      Pick<DocFrontendState, 'syncRetrying' | 'syncErrorMessage'>
    >;
  }
): () => void {
  let unavailable = true;
  let disposed = false;
  let snapshotKey: string | undefined;
  let snapshotAbort: AbortController | undefined;
  let releaseReadonly: (() => void) | undefined;
  const readySubscriptions = new Map<string, () => void>();
  const reconcile = () => {
    if (disposed) return;
    const docIds = collectLocalFirstPagePartDocIds(
      page.spaceDoc,
      page.workspace.id
    );
    const key = JSON.stringify(docIds);
    if (unavailable) {
      snapshotAbort?.abort();
      snapshotKey = undefined;
    } else if (docIds.length && snapshotKey !== key) {
      snapshotKey = key;
      snapshotAbort?.abort();
      const abort = new AbortController();
      snapshotAbort = abort;
      void waitForPagePartSnapshot(page, abort.signal).catch(error => {
        if (!abort.signal.aborted) {
          console.error('Page part snapshot barrier failed', error);
        }
      });
    }
    const wanted = new Set(docIds);
    for (const [id, release] of readySubscriptions) {
      if (!wanted.has(id)) {
        release();
        readySubscriptions.delete(id);
      }
    }
    for (const id of docIds) {
      if (readySubscriptions.has(id)) continue;
      const doc = page.workspace.getDoc(id);
      if (!doc) continue;
      const subscription = doc.slots.ready.subscribe(reconcile);
      readySubscriptions.set(id, () => subscription.unsubscribe());
    }
    const missing = docIds.some(id => !page.workspace.getDoc(id)?.ready);
    if (unavailable && missing) {
      releaseReadonly ??= page.retainReadonly();
    } else {
      releaseReadonly?.();
      releaseReadonly = undefined;
    }
  };
  page.spaceDoc.on('afterTransaction', reconcile);
  const releaseParts = page.pageStructure.observeParts(reconcile);
  const subscription = port.state$.subscribe(state => {
    unavailable = isDocSyncUnavailable(state);
    reconcile();
  });
  reconcile();
  return () => {
    disposed = true;
    snapshotAbort?.abort();
    page.spaceDoc.off('afterTransaction', reconcile);
    subscription.unsubscribe();
    releaseParts();
    for (const release of readySubscriptions.values()) release();
    releaseReadonly?.();
  };
}

type DatabaseRowBarrier = Parameters<
  typeof DatabaseBlockDataSource.registerRowShardWriteBarrier
>[1];

/** Local publications are durable; never waits for the server. */
export function createDatabaseRowWriteBarrier(
  port: DatabaseSubdocDocEngine,
  collection: Store['workspace'],
  isDisposed: () => boolean
): DatabaseRowBarrier {
  const barrier = createSystemDocBarrier(port, collection, isDisposed, 'local');
  return ({ rootDocId, rowShardDocIds, signal }) =>
    barrier({ rootDocId, docIds: rowShardDocIds, signal });
}

export function createSystemDocBarrier(
  port: DatabaseSubdocDocEngine,
  collection: Store['workspace'],
  isDisposed: () => boolean,
  authority: 'local' | 'server'
): SystemDocBarrier {
  return async ({ rootDocId, docIds: requestedDocIds, signal }) => {
    if (isDisposed() || signal.aborted) {
      throw new Error('Database row-shard snapshot barrier is disposed.');
    }
    if (
      typeof port.waitForUpdated !== 'function' ||
      (authority === 'server' && typeof port.pullRemote !== 'function')
    ) {
      throw new Error('Database document sync barrier is unavailable.');
    }

    const barrierAbort = new AbortController();
    const abortBarrier = () => barrierAbort.abort();
    signal.addEventListener('abort', abortBarrier, { once: true });
    const timeout = setTimeout(abortBarrier, 30_000);
    const docIds = [...new Set([rootDocId, ...requestedDocIds])];
    // Sync barrier mark for scripts/benchmarks/database-import-metrics.mjs, no document contents or errors.
    const markBarrier = (phase: string, docId = rootDocId) => {
      const name = `app-base-sync-barrier:${docId}:${phase}`;
      performance.clearMarks(name);
      performance.mark(name, {
        detail: {
          phase,
          docId,
          rootDocId,
          aborted: barrierAbort.signal.aborted,
        },
      });
    };
    const releasePriorities: Array<() => void> = [];
    try {
      for (const docId of docIds) {
        releasePriorities.push(retainDatabaseSubdoc(port, docId));
        releasePriorities.push(port.addPriority(docId, 110));
        loadDatabaseWorkspaceCollectionDoc(
          collection,
          docId,
          'row-shard-integrity-validation'
        );
      }
      // A local writer ACK is durability only. Server authority is the pull
      // below and is required only where readiness judges remote absence.
      await Promise.all(
        docIds.map(async docId => {
          if (authority === 'server') {
            await port.pullRemote!(docId, barrierAbort.signal);
            markBarrier('pulled', docId);
          }
          await port.waitForDocLoaded(docId, barrierAbort.signal);
          markBarrier('loaded', docId);
          await port.waitForUpdated!(docId, barrierAbort.signal);
          markBarrier('locally-durable', docId);
        })
      );
      for (const docId of docIds) acknowledgeDatabaseSubdoc(port, docId);
    } finally {
      markBarrier('finished');
      clearTimeout(timeout);
      signal.removeEventListener('abort', abortBarrier);
      releasePriorities.forEach(release => release());
    }
  };
}

function retainWorkspaceDatabaseDoc(
  port: DatabaseSubdocDocEngine,
  collection: Store['workspace'],
  docId: string
): () => void {
  return retainDatabaseSubdoc(port, docId, () => {
    void releaseAcknowledgedSystemDocCache(port, collection, docId).catch(
      error =>
        console.error(
          'Failed to release acknowledged database document cache',
          error
        )
    );
  });
}

/** The initialized WorkspaceScope owns exactly one collection-wide binding. */
export function bindWorkspaceDatabasePorts(
  workspaceService: WorkspaceService
): () => void {
  const { engine, docCollection } = workspaceService.workspace;
  const client = engine.client;
  if (!client || !docCollection) throw new PublicationError('FORMAT_MISMATCH');
  const disposeWriter = DatabaseBlockDataSource.registerWriterPorts(
    docCollection,
    {
      publication: engine.doc,
      rows: {
        mutate: (input, signal) => client.mutateDatabaseRows(input, signal),
        mutateGroup: (input, signal) =>
          client.mutateDatabaseRowGroup(input, signal),
        release: databaseRootDocId =>
          client.releaseDatabaseWriterSessions(databaseRootDocId),
      },
      importer: client.databaseImport,
      sync: {
        async waitForPublicationsSynced(receipts, signal, onDocSynced) {
          const docIds = [
            ...new Set(
              receipts.flatMap(receipt =>
                receipt.clocks.map(clock => clock.docId)
              )
            ),
          ];
          await Promise.all(
            docIds.map(async docId => {
              await engine.doc.flushAndWaitForSynced(docId, signal);
              onDocSynced?.(docId);
            })
          );
        },
      },
      inverse: createDatabaseRelationInversePlanner(workspaceService),
    }
  );
  const disposeBulk = DatabaseBlockDataSource.registerBulkProvider(
    docCollection,
    // User-facing CRUD commits locally in every workspace flavour. Remote
    // delivery follows the same publication sender as Create; a disconnected
    // server must not redirect Delete/Update to a second mutation authority.
    {
      mode: 'local',
      retain: async (docIds, signal) => {
        const releases = docIds.map(docId => {
          const release = retainDatabaseSubdoc(engine.doc, docId);
          loadDatabaseWorkspaceCollectionDoc(
            docCollection,
            docId,
            'database-row-window'
          );
          return release;
        });
        const release = () => releases.splice(0).forEach(dispose => dispose());
        try {
          await Promise.all(
            docIds.map(docId => engine.doc.waitForDocLoaded(docId, signal))
          );
          return release;
        } catch (error) {
          release();
          throw error;
        }
      },
      acknowledge: async (docIds, signal) => {
        await Promise.all(
          docIds.map(docId => engine.doc.waitForUpdated(docId, signal))
        );
      },
    }
  );
  const disposeProvider = DatabaseBlockDataSource.registerProjectionProvider(
    docCollection,
    {
      project: async (input, signal) => {
        await traceImportAwait(
          'projection.wait-for-updated',
          input.rootDocId,
          () => engine.doc.waitForUpdated(undefined, signal)
        );
        const generation = String(input.generation);
        const result = await traceImportAwait(
          'projection.worker-rpc',
          input.rootDocId,
          () => client.projectDatabaseRows({ ...input, generation }, signal)
        );
        if (result.generation !== generation)
          throw new Error('The database projection could not be verified.');
        return { ...result, generation: input.generation };
      },
      optionNames: async (input, signal) => {
        await engine.doc.waitForUpdated(undefined, signal);
        return client.readDatabaseOptionNames(input, signal);
      },
      subscribe: callback => {
        const disposeUpdates = client.subscribeDocUpdates(callback);
        const permissions = engine.permissionsChanged$?.subscribe(() =>
          callback()
        );
        return () => {
          disposeUpdates();
          permissions?.unsubscribe();
        };
      },
      reconciled: (docIds, signal) =>
        client.waitForDatabaseDocsReconciled(docIds, signal),
    }
  );
  const disposeLoader = registerSystemDocLoader(
    docCollection,
    (docId, context) => {
      const releaseDoc = retainWorkspaceDatabaseDoc(
        engine.doc,
        docCollection,
        docId
      );
      const releasePriority = engine.doc.addPriority(
        docId,
        systemDocLoadPriority(context)
      );
      loadDatabaseWorkspaceCollectionDoc(
        docCollection,
        docId,
        'database-row-window'
      );
      return () => {
        releasePriority();
        releaseDoc();
      };
    }
  );
  const disposeMissing =
    DatabaseBlockDataSource.registerSystemDocMissingObserver(
      docCollection,
      (docId, onMissing) => {
        const subscription = engine.doc.docState$(docId).subscribe(state => {
          if (isDatabaseRootMissing(state)) onMissing();
        });
        return () => subscription.unsubscribe();
      }
    );
  const disposePublicationView =
    DatabaseBlockDataSource.registerPublicationViewObserver(
      docCollection,
      createDatabasePublicationViewObserver(engine.doc)
    );
  let portsDisposed = false;
  const disposeWriteBarrier =
    DatabaseBlockDataSource.registerRowShardWriteBarrier(
      docCollection,
      createDatabaseRowWriteBarrier(
        engine.doc,
        docCollection,
        () => portsDisposed
      )
    );
  const disposeSnapshotBarrier = registerSystemDocBarrier(
    docCollection,
    createSystemDocBarrier(
      engine.doc,
      docCollection,
      () => portsDisposed,
      'server'
    )
  );
  return () => {
    if (portsDisposed) return;
    portsDisposed = true;
    disposeWriter();
    disposeWriteBarrier();
    disposeSnapshotBarrier();
    disposeBulk();
    disposeMissing();
    disposePublicationView();
    disposeLoader();
    disposeProvider();
  };
}

export function preloadDatabaseSubdocsForPage(
  page: Store,
  workspaceService: WorkspaceService
): () => void {
  const engine = workspaceService.workspace.engine;
  const seen = new Set<string>();
  const initialRowWarmDisposers = new Map<string, () => void>();
  const flushSeen = new Set<string>();
  const cleanups: Array<() => void> = [];
  let disposed = false;

  markDatabaseLoad('preload-start', {
    pageId: page.id,
    workspaceId: page.workspace.id,
  });

  const waitForLocalDocReady = (docId: string, reason: string) => {
    if (
      typeof engine.doc.waitForDocReady !== 'function' &&
      typeof engine.doc.waitForDocLoaded !== 'function'
    ) {
      return () => {};
    }
    const abort = new AbortController();
    const timeout = setTimeout(() => abort.abort(), 30_000);
    let released = false;
    const release = () => {
      if (released) return;
      released = true;
      clearTimeout(timeout);
      abort.abort();
      const index = cleanups.indexOf(release);
      if (index >= 0) cleanups.splice(index, 1);
    };
    cleanups.push(release);
    void waitForDatabaseDocReady(engine.doc, docId, abort.signal)
      .then(() => {
        markDatabaseLoad('preload-subdoc-local-ready-done', {
          docId,
          pageId: page.id,
          reason,
          workspaceId: page.workspace.id,
        });
      })
      .catch(error => {
        if (abort.signal.aborted || disposed) {
          return;
        }
        markDatabaseLoad('preload-subdoc-local-ready-failed', {
          docId,
          error: error instanceof Error ? error.message : String(error),
          pageId: page.id,
          reason,
          workspaceId: page.workspace.id,
        });
      })
      .finally(release);
    return release;
  };

  const enqueue = (
    docId: string,
    flushReason?: string,
    priority: number = 90
  ) => {
    if (disposed || seen.has(docId)) {
      return;
    }
    seen.add(docId);
    const releaseDoc = retainDatabaseSubdoc(engine.doc, docId);
    const releasePriority = engine.doc.addPriority(docId, priority);
    let released = false;
    const release = () => {
      if (released) return;
      released = true;
      releasePriority();
      releaseDoc();
    };
    cleanups.push(release);
    if (!isDatabaseRootDocId(docId, page.workspace.id))
      initialRowWarmDisposers.set(docId, release);
    const reason = flushReason ?? 'page-open';
    loadDatabaseWorkspaceDoc(page, docId, reason);
    waitForLocalDocReady(docId, reason);
    if (flushReason) {
      flushDatabaseSubdoc(docId, flushReason);
    }
  };

  function flushDatabaseSubdoc(docId: string, reason: string) {
    if (
      disposed ||
      flushSeen.has(docId) ||
      typeof engine.doc.flushAndWaitForSynced !== 'function'
    ) {
      return;
    }
    flushSeen.add(docId);
    const abort = new AbortController();
    const timeout = setTimeout(() => abort.abort(), 30_000);
    cleanups.push(() => {
      clearTimeout(timeout);
      abort.abort();
    });
    markDatabaseLoad('preload-subdoc-flush-start', {
      docId,
      pageId: page.id,
      reason,
      workspaceId: page.workspace.id,
    });
    void (engine.doc.pullRemote?.(docId, abort.signal) ?? Promise.resolve())
      .then(() => engine.doc.waitForUpdated?.(docId, abort.signal))
      .then(() => waitForDatabaseDocReady(engine.doc, docId, abort.signal))
      .then(() => engine.doc.flushAndWaitForSynced!(docId, abort.signal))
      .then(() => {
        markDatabaseLoad('preload-subdoc-flush-done', {
          docId,
          pageId: page.id,
          reason,
          workspaceId: page.workspace.id,
        });
      })
      .catch(error => {
        if (abort.signal.aborted || disposed) {
          return;
        }
        markDatabaseLoad('preload-subdoc-flush-failed', {
          docId,
          error: error instanceof Error ? error.message : String(error),
          pageId: page.id,
          reason,
          workspaceId: page.workspace.id,
        });
      })
      .finally(() => clearTimeout(timeout));
  }

  const cleanupRowShardDocLoader = registerSystemDocLoader(
    page,
    (docId, context) => {
      const releaseDoc = retainWorkspaceDatabaseDoc(
        engine.doc,
        page.workspace,
        docId
      );
      const releasePriority = engine.doc.addPriority(
        docId,
        systemDocLoadPriority(context)
      );
      loadDatabaseWorkspaceDoc(page, docId, 'row-shard-loader');
      const releaseReady = waitForLocalDocReady(docId, 'row-shard-loader');
      flushDatabaseSubdoc(docId, 'row-shard-loader');
      initialRowWarmDisposers.get(docId)?.();
      initialRowWarmDisposers.delete(docId);
      let released = false;
      return () => {
        if (released) return;
        released = true;
        releaseReady();
        releasePriority();
        releaseDoc();
      };
    }
  );
  cleanups.push(cleanupRowShardDocLoader);

  // A part with a local write the local store has not confirmed yet
  // (waitForUpdated) stays in memory.
  const unconfirmedPartWrites = new Map<string, number>();
  cleanups.push(
    page.pageStructure.onPartTransaction((transaction, partId) => {
      if (
        !transaction.local ||
        transaction.changed.size === 0 ||
        partId === PAGE_DOCUMENT_PART_ID
      )
        return;
      const write = (unconfirmedPartWrites.get(partId) ?? 0) + 1;
      unconfirmedPartWrites.set(partId, write);
      const docId = createServerPagePartDocId({
        workspaceId: page.workspace.id,
        pageId: page.id,
        partId,
      });
      engine.doc
        .waitForUpdated(docId)
        .then(() => {
          if (unconfirmedPartWrites.get(partId) === write)
            unconfirmedPartWrites.delete(partId);
        })
        .catch(error =>
          console.error('Page part write was not confirmed locally', error)
        );
    }),
    page.pageStructure.addUnloadGuard(partId =>
      unconfirmedPartWrites.has(partId)
    )
  );

  const cleanupSystemDocMissingObserver =
    DatabaseBlockDataSource.registerSystemDocMissingObserver(
      page,
      (docId, onMissing) => {
        const subscription = engine.doc.docState$(docId).subscribe(state => {
          if (isDatabaseRootMissing(state)) onMissing();
        });
        return () => subscription.unsubscribe();
      }
    );
  cleanups.push(cleanupSystemDocMissingObserver);

  cleanups.push(
    DatabaseBlockDataSource.registerPublicationViewObserver(
      page,
      createDatabasePublicationViewObserver(engine.doc)
    )
  );

  const cleanupRowShardSnapshotBarrier = registerSystemDocBarrier(
    page,
    createSystemDocBarrier(engine.doc, page.workspace, () => disposed, 'server')
  );
  cleanups.push(cleanupRowShardSnapshotBarrier);
  cleanups.push(
    DatabaseBlockDataSource.registerRowShardWriteBarrier(
      page,
      createDatabaseRowWriteBarrier(engine.doc, page.workspace, () => disposed)
    )
  );

  const {
    discovery,
    localFirstSubdocIds: pageLocalFirstSubdocIds,
    subdocIds: pageSubdocIds,
  } = collectInitialDatabaseSubdocIdsAndDiscoveryFromPage(page);
  markDatabaseSubdocDiscovery(page, discovery, pageSubdocIds.length);
  markDatabaseLoad('preload-page-collected', {
    count: pageSubdocIds.length,
    pageId: page.id,
    workspaceId: page.workspace.id,
  });
  pageSubdocIds.forEach(docId => enqueue(docId));
  pageLocalFirstSubdocIds.forEach(docId => enqueue(docId, undefined, 20));
  cleanups.push(bindPagePartOfflineBarrier(page, engine.doc));
  // The documents are registered now, so the parts among them are observed.
  const cleanupDiscoveryObserver = observeDatabaseSubdocDiscovery(
    page,
    enqueue,
    () => seen.size,
    pageSubdocIds
  );

  engine.indexer
    .getDatabaseSubdocIds(page.id)
    .then(docIds => {
      const rootDocIds = docIds.filter(docId =>
        isDatabaseRootDocId(docId, page.workspace.id)
      );
      markDatabaseLoad('preload-indexer-collected', {
        count: rootDocIds.length,
        disposed,
        enqueuedCount: seen.size,
        rawCount: docIds.length,
        pageId: page.id,
        workspaceId: page.workspace.id,
      });
      rootDocIds.forEach(docId => enqueue(docId));
      markDatabaseLoad('preload-end', {
        disposed,
        enqueuedCount: seen.size,
        indexerCount: rootDocIds.length,
        pageCount: pageSubdocIds.length,
        rawIndexerCount: docIds.length,
        pageId: page.id,
        workspaceId: page.workspace.id,
      });
    })
    .catch(error => {
      markDatabaseLoad('preload-end', {
        disposed,
        enqueuedCount: seen.size,
        error: error instanceof Error ? error.message : 'indexer failed',
        indexerFailed: true,
        pageCount: pageSubdocIds.length,
        pageId: page.id,
        workspaceId: page.workspace.id,
      });
      // Index hints are best-effort; loaded page/root-doc data remains canonical.
    });

  return () => {
    disposed = true;
    cleanupDiscoveryObserver();
    markDatabaseLoad('preload-disposed', {
      enqueuedCount: seen.size,
      pageId: page.id,
      workspaceId: page.workspace.id,
    });
    cleanups.splice(0).forEach(cleanup => cleanup());
  };
}

export function preloadDatabaseSubdocsForDataSourceRefs(
  refs: readonly DatabaseSubdocDataSourceRef[],
  workspaceService: WorkspaceService
): () => void {
  const engine = workspaceService.workspace.engine;
  const docIds = collectDatabaseSubdocIdsFromDataSourceRefs(
    refs,
    workspaceService.workspace.id,
    workspaceService.workspace.docCollection
  );
  const cleanups: Array<() => void> = [];
  let disposed = false;

  markDatabaseLoad('preload-datasource-refs-start', {
    count: docIds.length,
    refCount: refs.length,
    workspaceId: workspaceService.workspace.id,
  });

  for (const docId of docIds) {
    if (disposed) break;
    cleanups.push(retainDatabaseSubdoc(engine.doc, docId));
    cleanups.push(engine.doc.addPriority(docId, 90));
  }

  return () => {
    disposed = true;
    markDatabaseLoad('preload-datasource-refs-disposed', {
      count: docIds.length,
      workspaceId: workspaceService.workspace.id,
    });
    cleanups.splice(0).forEach(cleanup => cleanup());
  };
}

/** Preload schema/manifest roots only. Row shards are selected later by the
 * local query window and must never be eagerly materialized for an app. */
export function preloadDatabaseRootsForDataSourceRefs(
  refs: readonly DatabaseSubdocDataSourceRef[],
  workspaceService: WorkspaceService
): () => void {
  const engine = workspaceService.workspace.engine;
  const docIds = collectDatabaseRootSubdocIdsFromDatabaseBlocks(
    refs.map(dataSourceRefToDatabaseBlockIdentity),
    workspaceService.workspace.id
  );
  const cleanups: Array<() => void> = [];
  for (const docId of docIds) {
    cleanups.push(retainDatabaseSubdoc(engine.doc, docId));
    cleanups.push(engine.doc.addPriority(docId, 90));
  }
  return () => cleanups.splice(0).forEach(cleanup => cleanup());
}

/** Relation planning reads persisted bases and uses the existing bounded doc loader. */
export function createDatabaseRelationInversePlanner(
  workspaceService: WorkspaceService
): DatabaseRelationInversePlanner {
  const { engine, docCollection } = workspaceService.workspace;
  const read = async (docId: string, signal?: AbortSignal) => {
    let [base] = await engine.doc.readPublicationBase([docId], signal);
    if (!base?.bin) {
      if (globalThis.navigator?.onLine === false)
        throw new DatabasePublicationBaseUnavailable();
      loadDatabaseWorkspaceCollectionDoc(
        docCollection,
        docId,
        'database-relation-inverse'
      );
      await engine.doc.waitForDocLoaded(docId, signal);
      await engine.doc.pullRemote(docId, signal);
      await engine.doc.waitForUpdated(docId, signal);
      [base] = await engine.doc.readPublicationBase([docId], signal);
      if (!base?.bin) throw new DatabasePublicationBaseUnavailable();
    }
    return base;
  };
  return {
    plan: async (input, signal) => {
      const readers: YDoc[] = [];
      const open = async (docId: string) => {
        const base = await read(docId, signal);
        const doc = new YDoc({ guid: docId });
        readers.push(doc);
        applyUpdate(doc, base.bin!);
        return doc;
      };
      // A page keeps each database block in the part its entry names.
      const pageDatabaseBlock = async (doc: YDoc, id: string) => {
        const parts = new Map<string, YDoc>();
        let blockIds: string[];
        for (;;) {
          signal?.throwIfAborted();
          try {
            blockIds = pageBlockIdsOfKind(
              pageReadSource(doc, parts),
              'affine:database'
            );
            break;
          } catch (error) {
            if (!(error instanceof PageReadPartMissingError)) throw error;
            parts.set(
              error.partId,
              await open(
                createServerPagePartDocId({
                  workspaceId: workspaceService.workspace.id,
                  pageId: doc.guid,
                  partId: error.partId,
                })
              )
            );
          }
        }
        const page = readPage(pageReadSource(doc, parts));
        return blockIds
          .map(blockId => ({ blockId, value: page.blocks.get(blockId) }))
          .find(
            ({ blockId, value }) =>
              value instanceof YMap &&
              (blockId === id || value.get('prop:databaseId') === id)
          )?.value;
      };
      // A database root keeps its block in its own document.
      const databaseBlock = async (doc: YDoc, id: string) => {
        const block = isDatabaseRootDocId(
          doc.guid,
          workspaceService.workspace.id
        )
          ? doc.getMap('blocks').get(id)
          : await pageDatabaseBlock(doc, id);
        if (!(block instanceof YMap))
          throw new PublicationError('FORMAT_MISMATCH');
        return block;
      };
      try {
        const source = await databaseBlock(
          await open(input.source.docId),
          input.source.databaseId
        );
        const rawColumns = source.get('prop:columns');
        const columns =
          rawColumns instanceof YArray ? rawColumns.toJSON() : rawColumns;
        if (!Array.isArray(columns))
          throw new PublicationError('FORMAT_MISMATCH');
        const planned: DatabaseRelationInverseEdit[] = [];
        const targets = new Map<
          string,
          {
            docId: string;
            databaseId: string;
            manifest: NonNullable<
              ReturnType<typeof normalizeDatabaseShardManifest>
            >;
          }
        >();
        const verified = new Set<string>();
        for (const edit of input.edits) {
          const column = columns.find(column => column.id === edit.propertyId);
          const data = column?.data;
          if (!data?.inverseFieldId) continue;
          const databaseId = String(data.targetDatabaseId ?? '');
          if (!databaseId) throw new PublicationError('FORMAT_MISMATCH');
          const pageId =
            typeof data.targetDocId === 'string' && data.targetDocId
              ? data.targetDocId
              : findDatabaseByBlockId(databaseId)?.docId;
          if (!pageId) throw new DatabasePublicationBaseUnavailable();
          const key = JSON.stringify([pageId, databaseId]);
          let target = targets.get(key);
          if (!target) {
            const host = await databaseBlock(await open(pageId), databaseId);
            const rootDocId = host.get('prop:databaseRootDocId');
            if (typeof rootDocId !== 'string' || !rootDocId)
              throw new PublicationError('FORMAT_MISMATCH');
            const root = await databaseBlock(await open(rootDocId), databaseId);
            // The shard entries live in the directory part documents.
            const parts = new Map<string, YDoc>();
            for (const partDocId of readDatabaseShardDirectoryBlockPartDocIds(
              root
            ))
              parts.set(partDocId, await open(partDocId));
            const manifest = readDatabaseShardDirectoryBlock(
              root,
              partDocId => {
                const part = parts.get(partDocId);
                return part && databaseShardDirectoryPartMap(part);
              }
            );
            if (!manifest) throw new PublicationError('FORMAT_MISMATCH');
            target = {
              docId: rootDocId,
              databaseId: String(root.get('prop:databaseId') ?? databaseId),
              manifest,
            };
            targets.set(key, target);
          }
          const remove = edit.before.filter(id => !edit.after.includes(id));
          const add = edit.after.filter(id => !edit.before.includes(id));
          for (const rowId of new Set([...remove, ...add])) {
            // Every shard the fingerprints name is read; a false positive
            // only costs that read.
            const candidates = selectDatabaseShardsMayContainRow(
              target.manifest.shards,
              rowId
            ).filter(shard => !verified.has(shard.serverDocId));
            await Promise.all(
              candidates.map(shard => read(shard.serverDocId, signal))
            );
            for (const shard of candidates) verified.add(shard.serverDocId);
            planned.push({
              target: { docId: target.docId, databaseId: target.databaseId },
              rowId,
              fieldId: String(data.inverseFieldId),
              remove: remove.includes(rowId) ? [edit.rowId] : [],
              add: add.includes(rowId) ? [edit.rowId] : [],
              replace: data.inverseCardinality === 'one',
            });
          }
        }
        return planned;
      } finally {
        for (const reader of readers) reader.destroy();
      }
    },
  };
}
